/* Ikigai Cases site search, v3.
 *
 * WHAT IT DOES
 *   Overlay (#searchPopup): Shopify predictive search (/search/suggest.json) + intent answers,
 *   typo fixes, query relaxation, menu names and live prices, colour-aware photos.
 *   Results page (#IkSearchPage): intent answer / finder on top, typo and relaxed-query redirects.
 *   Config and copy: #SearchRules (snippets/search-rules.liquid).
 *   Photos, live prices, variants: /collections/all?view=ik-search-data (one cached request).
 *
 * A/B AND VERSIONS
 *   Variant, in order: ?ikgsearch=legacy|v3 (QA, session) > Intelligems Test 47 group
 *   (igData.user.getTestGroup) > CFG.defaultVariant ('legacy'). window.IKG_SEARCH counts only
 *   when no experiment id is configured. Checked on boot and on every open. 'legacy' leaves
 *   the old popup and results page in charge; this script then only records events.
 *   The new results page lives on templates/search.ik.json (?view=ik); searchUrl() adds it.
 *   Flags: window.IKG_SEARCH_FLAGS = {typo:false} or ?ikgsearchflags=typo:0 to test one feature.
 *   New version: bump "version" in search-rules; ship a new variant name behind html.ikS-<name>.
 *
 * MONITORING
 *   Events go to GTM (dataLayer "ikigai_<name>"), Shopify customer events, and gtag only when
 *   tracking.ga4 = 'direct':
 *     search_open           trigger
 *     search                one per search session, sent when the shopper clicks, submits,
 *                           closes or leaves: search_term, search_term_used, intent,
 *                           results_count, links_count, outcome, refinements, corrected, relaxed
 *     search_no_results     same params, when nothing matched
 *     select_content        content_type=search_result, item_id, section, position, search_term
 *     search_results_view   results page: search_term, results_count, no_results
 *     search_error          stage, message
 *   All carry search_variant, search_version, search_flags_off.
 *   Console: ikSearch.debug(true) logs events; ikSearch.log holds the last 100;
 *   ikSearch.explain('fish oil') shows how a query is read.
 *
 * THEME HOOKS: .site-header .searchIco, .closeSearch, body.showOverly and
 * #searchPopup.active. The capture listener focuses v3 inside the user gesture;
 * theme.Search still owns legacy opening and shared backdrop closing.
 * Configure experiment IDs in SearchRules before starting an Intelligems test.
 * Unassigned exposure is retained; search_assignment reconciles late assignment.
 */
(function () {
  'use strict';
  if (window.ikSearch && window.ikSearch.ready) { return; }

  /* ---------- config ---------- */
  function readJSON(id) {
    var el = document.getElementById(id);
    if (!el) { return null; }
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }
  var CFG = readJSON('SearchRules') || {};
  var VERSION = CFG.version || '3';
  var DISPLAY = CFG.display || {};
  var LINKS = CFG.links || {};
  var INTENTS = CFG.intents || [];
  // Service answers live in Shopify FAQ metaobjects (search-rules builds CFG.faq); resolve them once.
  // A missing FAQ shows no answer text (never a guessed one) and logs which FAQ is missing.
  (function () {
    var faq = CFG.faq || {};
    INTENTS.forEach(function (it) {
      ['faq', 'noteFaq'].forEach(function (key) {
        if (!it[key]) { return; }
        var text = faq[it[key]];
        if (!text) { console.error('[ik-search] FAQ answer missing: ' + it[key]); return; }
        if (key === 'faq') { it.answer = text; } else { it.note = text; }
      });
    });
  })();
  var PRIORITY = CFG.priority || {};
  var STOP = CFG.stop || [];
  var HIDDEN = CFG.hiddenProducts || [];
  var CONTEXT = CFG.context || {};
  var EXPERIMENT = CFG.experiment || {};
  var QA_KEY = 'ikgSearch_' + VERSION + '_' + (EXPERIMENT.id || 'qa');
  var exposureCounter = 0, exposures = [];
  var pageId = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

  function ss(k, v) {
    try {
      if (v === undefined) { return window.sessionStorage.getItem(k); }
      if (v === null) { window.sessionStorage.removeItem(k); } else { window.sessionStorage.setItem(k, v); }
    } catch (e) {}
    return null;
  }
  function ls(k, v) {
    try {
      if (v === undefined) { return window.localStorage.getItem(k); }
      if (v === null) { window.localStorage.removeItem(k); } else { window.localStorage.setItem(k, v); }
    } catch (e) {}
    return null;
  }

  /* ---------- variant + flags ---------- */
  function assignment() {
    if (EXPERIMENT.id) {
      try {
        var user = window.igData && window.igData.user;
        var group = user && typeof user.getTestGroup === 'function' ? user.getTestGroup(EXPERIMENT.id) : null;
        if (group) {
          var v = group.id === EXPERIMENT.legacyGroupId ? 'legacy' : group.id === EXPERIMENT.v3GroupId ? 'v3' : 'unknown';
          return { variant: v, group: group.id, source: 'intelligems' };
        }
      } catch (e) {}
      return { variant: 'unknown', group: '', source: 'pending' };
    }
    var value = window.IKG_SEARCH;
    return { variant: value === 'legacy' || value === 'v3' ? value : 'unknown', group: '', source: value ? 'variation' : 'pending' };
  }
  function variant() {
    var m = window.location.search.match(/[?&]ikgsearch=(legacy|v3)(&|$)/);
    var v = (m && m[1]) || ss(QA_KEY) || assignment().variant;
    // Not assigned (test not started, Intelligems blocked or still loading): show the default arm.
    if (v !== 'legacy' && v !== 'v3') { v = CFG.defaultVariant === 'v3' ? 'v3' : 'legacy'; }
    return v;
  }
  function applyVariant() {
    if (document.querySelector('#searchPopup.active')) { return document.documentElement.classList.contains('ikS-legacy') ? 'legacy' : 'v3'; }
    var v = variant(), d = document.documentElement;
    d.classList.toggle('ikS-legacy', v === 'legacy');
    d.classList.toggle('ikS-v3', v !== 'legacy');
    return v;
  }
  var FLAGS = {};
  var FLAGS_OFF = 'none';
  function readFlags() {
    var f = {}, k, off = [];
    var defs = CFG.flags || {};
    for (k in defs) { if (Object.prototype.hasOwnProperty.call(defs, k)) { f[k] = !!defs[k]; } }
    String(ss('ikgSearchFlags') || '').split(',').forEach(function (p) {
      var kv = p.split(':');
      if (kv[0] && kv.length === 2) { f[kv[0]] = kv[1] !== '0' && kv[1] !== 'false'; }
    });
    var w = window.IKG_SEARCH_FLAGS || {};
    for (k in w) { if (Object.prototype.hasOwnProperty.call(w, k)) { f[k] = !!w[k]; } }
    for (k in f) { if (!f[k]) { off.push(k); } }
    FLAGS = f;
    FLAGS_OFF = off.length ? off.sort().join(',') : 'none';
  }
  readFlags();

  /* ---------- helpers ---------- */
  function esc(t) {
    return String(t == null ? '' : t).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function norm(t) { return String(t || '').toLowerCase().replace(/[’']/g, '').replace(/[^a-z0-9]+/g, ' ').trim(); }
  function words(t) { var n = norm(t); return n ? n.split(' ') : []; }
  function plainText(html) { var el = document.createElement('div'); el.innerHTML = html; return el.textContent || ''; }
  function localUrl(url) {
    var root = CFG.root || '/';
    if (root.charAt(root.length - 1) !== '/') { root += '/'; }
    return /^\/(products|collections|pages|search)(\/|\?|$)/.test(url) && url.indexOf(root) !== 0 ? root + url.slice(1) : url;
  }
  Object.keys(LINKS).forEach(function (key) { LINKS[key].url = localUrl(LINKS[key].url); });
  function sized(url, w) {
    if (!url) { return ''; }
    if (/[?&]width=/.test(url)) { return url; }
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }
  function urlKey(u) { return String(u || '').split('?')[0].split('#')[0].replace(/\/$/, '').toLowerCase(); }
  function searchUrl(term, from) {
    // view=ik selects templates/search.ik.json, the only template that renders the new results page.
    return (CFG.searchUrl || localUrl('/search')) + '?q=' + encodeURIComponent(term) + '&view=ik&type=product,page,article&options%5Bprefix%5D=last' +
      (from ? '&ik_from=' + encodeURIComponent(from) : '');
  }
  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ---------- tracking ---------- */
  var api = window.ikSearch = window.ikSearch || {};
  api.ready = true;
  api.version = VERSION;
  api.log = [];
  var DEBUG = ss('ikgSearchDebug') === '1';
  function expose(rendered) {
    var a = assignment();
    var exposure = { variant: rendered, id: pageId + '-' + (++exposureCounter), assignedAtOpen: a.variant, reportedAssignment: a.variant };
    exposures.push(exposure);
    if (exposures.length > 100) { exposures.shift(); }
    return exposure;
  }
  function track(name, params, forceVariant, exposure) {
    var rendered = document.querySelector('#searchPopup.active') ? (document.documentElement.classList.contains('ikS-legacy') ? 'legacy' : 'v3') : (document.getElementById('IkSearchPage') ? 'v3' : variant());
    var a = assignment(), shown = forceVariant || (exposure && exposure.variant) || rendered;
    var p = { search_variant: shown, search_version: VERSION, search_flags_off: FLAGS_OFF,
      search_assigned_variant: a.variant, search_assignment_source: a.source,
      search_experiment_id: EXPERIMENT.id || '', search_group_id: a.group,
      search_qa_override: /[?&]ikgsearch=(legacy|v3)(&|$)/.test(window.location.search) || ss(QA_KEY) ? 1 : 0,
      search_crossover: a.variant === 'unknown' ? 'unknown' : (a.variant !== shown ? 1 : 0) };
    if (exposure) { p.search_session_id = exposure.id; p.search_assignment_at_open = exposure.assignedAtOpen; }
    for (var k in params) { if (Object.prototype.hasOwnProperty.call(params, k)) { p[k] = params[k]; } }
    api.log.push({ at: Date.now(), event: name, params: p });
    if (api.log.length > 100) { api.log.shift(); }
    if (DEBUG && window.console) { console.log('[ikSearch]', name, p); }
    // One GA4 route: GTM by default. Set tracking.ga4='direct' only if GTM
    // does not forward the ikigai_* custom events to the same property.
    var direct = CFG.tracking && CFG.tracking.ga4 === 'direct';
    try { if (direct && typeof window.gtag === 'function') { window.gtag('event', name, p); } } catch (e) {}
    try {
      var dl = { event: 'ikigai_' + name };
      dl.search_ga4_route = direct ? 'direct' : 'gtm';
      for (var j in p) { if (Object.prototype.hasOwnProperty.call(p, j)) { dl[j] = p[j]; } }
      (window.dataLayer = window.dataLayer || []).push(dl);
    } catch (e) {}
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish('ikigai_' + name, p);
      }
    } catch (e) {}
  }
  function reconcileAssignments() {
    var a = assignment();
    exposures.forEach(function (exposure) {
      if (a.variant !== 'unknown' && exposure.reportedAssignment !== a.variant) {
        exposure.reportedAssignment = a.variant;
        track('search_assignment', {}, exposure.variant, exposure);
      }
    });
    if (leaveNewResultsPage()) { return; }
    if (!document.querySelector('#searchPopup.active')) { applyVariant(); }
  }
  window.addEventListener('ig:ready', reconcileAssignments);

  /* ---------- data feed (photos, live prices, variants) ---------- */
  var DATA = null, dataP = null, dataAt = 0;
  var DATA_TTL = 30 * 60 * 1000;
  var DATA_KEY = 'ikS_data_' + VERSION + '_' + ['theme', 'currency', 'country', 'market', 'locale'].map(function (k) { return CONTEXT[k] || ''; }).join('_');
  function sameContext(context) {
    return context && ['theme', 'currency', 'country', 'market', 'locale'].every(function (k) { return String(context[k] || '') === String(CONTEXT[k] || ''); });
  }
  function validProducts(products) {
    return products && typeof products === 'object' && !Array.isArray(products) && Object.keys(products).every(function (h) {
      var p = products[h];
      return p && typeof p.s === 'boolean' && typeof p.a === 'boolean' && typeof p.t === 'string' && typeof p.u === 'string' && typeof p.i === 'string' && typeof p.price === 'string' && Array.isArray(p.v) && p.v.every(function (v) {
        return Array.isArray(v) && v.length === 4 && typeof v[0] === 'number' && typeof v[1] === 'string' && typeof v[2] === 'string' && typeof v[3] === 'boolean';
      });
    });
  }
  function freshData() {
    if (DATA && Date.now() - dataAt >= DATA_TTL) { DATA = null; dataAt = 0; buildVocab(); }
    return DATA;
  }
  function fetchTimed(url, json) {
    var controller = window.AbortController ? new AbortController() : null;
    var timer;
    return Promise.race([
      fetch(url, { credentials: 'same-origin', signal: controller ? controller.signal : undefined }).then(function (r) {
        if (!r.ok) { throw new Error('http ' + r.status); }
        return json ? r.json() : r.text();
      }),
      new Promise(function (_, reject) { timer = setTimeout(function () { if (controller) { controller.abort(); } reject(new Error('timeout')); }, 5000); })
    ]).finally(function () { clearTimeout(timer); });
  }
  function loadData() {
    if (freshData()) { return Promise.resolve(DATA); }
    if (dataP) { return dataP; }
    try {
      var c = JSON.parse(ss(DATA_KEY) || 'null');
      if (c && validProducts(c.d) && sameContext(c.context) && Date.now() >= c.at && Date.now() - c.at < DATA_TTL) { DATA = c.d; dataAt = c.at; buildVocab(); return Promise.resolve(DATA); }
    } catch (e) {}
    dataP = fetchTimed(localUrl('/collections/all') + '?view=ik-search-data&v=' + encodeURIComponent(VERSION))
      .then(function (t) {
        var parsed = JSON.parse(t);
        if (!parsed || parsed.v !== VERSION || !sameContext(parsed.context) || !validProducts(parsed.p)) { throw new Error('invalid feed context or shape'); }
        DATA = parsed.p; dataAt = Date.now();
        ss(DATA_KEY, JSON.stringify({ at: dataAt, d: DATA, context: CONTEXT }));
        buildVocab();
        return DATA;
      })
      .catch(function (e) {
        track('search_error', { stage: 'data', message: String((e && e.message) || e).slice(0, 80) });
        return {};
      }).finally(function () { dataP = null; });
    return dataP;
  }

  /* ---------- typo fixes ---------- */
  var VOCAB = {}, VLIST = [];
  function addWords(str, weight) {
    words(str).forEach(function (w) { if (w.length > 1 && !(VOCAB[w] >= weight)) { VOCAB[w] = weight; } });
  }
  function buildVocab() {
    VOCAB = {};
    addWords(CFG.common, 1);
    addWords(CFG.vocab, 2);
    STOP.forEach(function (w) { addWords(w, 1); });
    INTENTS.forEach(function (it) { (it.match || []).forEach(function (p) { addWords(p, 3); }); });
    Object.keys(DISPLAY).forEach(function (h) { addWords(DISPLAY[h].name, 4); addWords(h, 2); });
    if (DATA) {
      Object.keys(DATA).forEach(function (h) {
        addWords(DATA[h].t, 3);
        (DATA[h].v || []).forEach(function (v) { addWords(v[1], 2); });
      });
    }
    VLIST = Object.keys(VOCAB);
  }
  buildVocab();
  // Optimal string alignment distance (Damerau-Levenshtein with adjacent swaps), capped.
  function osa(a, b, max) {
    var la = a.length, lb = b.length, i, j;
    if (Math.abs(la - lb) > max) { return max + 1; }
    var d = [];
    for (i = 0; i <= la; i++) { d[i] = [i]; }
    for (j = 0; j <= lb; j++) { d[0][j] = j; }
    for (i = 1; i <= la; i++) {
      var rowMin = 99;
      for (j = 1; j <= lb; j++) {
        var cost = a[i - 1] === b[j - 1] ? 0 : 1;
        var v = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) { v = Math.min(v, d[i - 2][j - 2] + 1); }
        d[i][j] = v;
        if (v < rowMin) { rowMin = v; }
      }
      if (rowMin > max) { return max + 1; }
    }
    return d[la][lb];
  }
  function correctWord(w) {
    if (w.length < 4 || /\d/.test(w) || VOCAB[w]) { return w; }
    for (var i = 0; i < VLIST.length; i++) { if (VLIST[i].indexOf(w) === 0) { return w; } } // still typing
    var max = w.length >= 8 ? 2 : 1, best = null, bd = 99, bw = 0;
    VLIST.forEach(function (v) {
      if (v.length < 3 || v[0] !== w[0] || Math.abs(v.length - w.length) > max) { return; }
      var dd = osa(w, v, max);
      if (dd <= max && (dd < bd || (dd === bd && (VOCAB[v] > bw || (VOCAB[v] === bw && v.length > best.length))))) { best = v; bd = dd; bw = VOCAB[v]; }
    });
    return best || w;
  }
  function correct(term) {
    if (!FLAGS.typo) { return term; }
    var ws = words(term), changed = false;
    var out = ws.map(function (w) { var c = correctWord(w); if (c !== w) { changed = true; } return c; });
    // Split names typed as two words ("mag nano" -> "magnano").
    for (var i = 0; i < out.length - 1; i++) {
      var j = out[i] + out[i + 1];
      if (VOCAB[j] >= 4) { out.splice(i, 2, j); changed = true; }
      else if (j.length >= 6 && VOCAB[correctWord(j)] >= 4 && correctWord(j) !== j) { out.splice(i, 2, correctWord(j)); changed = true; }
    }
    return changed ? out.join(' ') : term;
  }
  function relax(term) {
    var ws = words(term);
    var keep = ws.filter(function (w) { return STOP.indexOf(w) === -1 && w.length > 1; });
    return keep.length && keep.length < ws.length ? keep.join(' ') : '';
  }

  /* ---------- intents ---------- */
  INTENTS.forEach(function (it) {
    it._m = (it.match || []).map(function (p) { return words(p); }).filter(function (p) { return p.length; });
    it._u = (it.unless || []).map(function (p) { return words(p); }).filter(function (p) { return p.length; });
    it._s = it.stems || [];
  });
  function hasPhrase(ws, pw) {
    outer: for (var i = 0; i <= ws.length - pw.length; i++) {
      for (var j = 0; j < pw.length; j++) { if (ws[i + j] !== pw[j]) { continue outer; } }
      return true;
    }
    return false;
  }
  function matchIntents(term) {
    var ws = words(term);
    if (!ws.length || norm(term).length < 2) { return []; }
    return INTENTS.filter(function (it) {
      var hit = it._m.some(function (p) { return hasPhrase(ws, p); }) ||
        it._s.some(function (s) { return ws.some(function (w) { return w.indexOf(s) === 0; }); });
      if (!hit) { return false; }
      return !it._u.some(function (p) { return hasPhrase(ws, p); });
    });
  }

  /* ---------- ranking ---------- */
  function rank(handle) {
    var i = (PRIORITY.core || []).indexOf(handle);
    if (i > -1) { return 101 + i; }
    i = (PRIORITY.bundles || []).indexOf(handle);
    if (i > -1) { return 151 + i; }
    i = (PRIORITY.addons || []).indexOf(handle);
    if (i > -1) { return 301 + i; }
    return 200;
  }
  function sigWords(term) {
    var ws = words(term), sig = ws.filter(function (w) { return STOP.indexOf(w) === -1; });
    return sig.length ? sig : ws;
  }
  function nameOf(p) { return (DISPLAY[p.handle] && DISPLAY[p.handle].name) || p.title; }
  function rankProducts(list, term) {
    // Generic words (pill, case, box) do not rank; if that is all there is, core cases lead.
    var qw = words(term).filter(function (w) { return STOP.indexOf(w) === -1; }), qj = qw.join(' ');
    return list.map(function (p, i) {
      var nw = words(nameOf(p) + ' ' + p.title), joined = nw.join(''), s = 0;
      qw.forEach(function (w) {
        if (nw.indexOf(w) > -1) { s += 6; }
        else if (nw.some(function (x) { return x.indexOf(w) === 0; })) { s += 4; }
        else if (w.length >= 4 && joined.indexOf(w) > -1) { s += 1; }
      });
      if (qj && sigWords(nameOf(p)).join(' ') === qj) { s += 10; }
      return { p: p, s: s, a: p.available === false ? 1 : 0, r: rank(p.handle), i: i };
    }).sort(function (a, b) { return a.a - b.a || b.s - a.s || a.r - b.r || a.i - b.i; })
      .map(function (x) { return x.p; });
  }
  // Same policy as search-rules 'check': tagged Active, or a curated product in the display rows.
  function eligibleTags(p) {
    if (!p.tags) { return !!DISPLAY[p.handle]; } // no tag data: show curated products only (fail closed)
    return DISPLAY[p.handle] ? true : p.tags.indexOf(CFG.requiredTag || 'Active') > -1;
  }
  function visibleProduct(p) { return HIDDEN.indexOf(p.handle) === -1 && p.type !== 'Engraving' && eligibleTags(p) && !(freshData() && DATA[p.handle] && DATA[p.handle].s === false); }

  /* ---------- cards ---------- */
  // A query word found in a variant title (a colour, a size) links to that variant and shows its photo.
  function variantFor(handle, sp, term) {
    var name = words(DISPLAY[handle] ? DISPLAY[handle].name : (sp && sp.title) || handle);
    var qw = sigWords(term).filter(function (w) { return w.length > 2 && name.indexOf(w) === -1; });
    if (!qw.length) { return null; }
    var list = [];
    var d = DATA && DATA[handle];
    if (d && d.v) { list = d.v.map(function (v) { return { id: v[0], title: v[1], img: v[2], a: v[3] }; }); }
    // Suggestions do not carry case_color visibility. Without the validated
    // feed, link to the product rather than a potentially hidden colour.
    if (!list.length) { return null; }
    var hit = null;
    list.forEach(function (v) {
      if (hit && hit.a) { return; }
      var vw = words(v.title);
      if (qw.some(function (w) { return vw.indexOf(w) > -1; }) && (!hit || v.a)) { hit = v; }
    });
    return hit;
  }
  function cardData(handle, sp, term) {
    var d = freshData() && DATA[handle], disp = DISPLAY[handle] || {};
    var c = {
      handle: handle,
      name: disp.name || (d && d.t) || (sp && sp.title) || handle,
      line: disp.line || '',
      url: localUrl(urlKey((d && d.u) || (sp && sp.url) || ('/products/' + handle))),
      img: (d && d.i) || (sp && sp.featured_image && sp.featured_image.url ? sized(sp.featured_image.url, 400) : ''),
      price: d || sp ? disp.price || '' : '',
      available: d ? d.a : (sp && typeof sp.available === 'boolean' ? sp.available : null),
      hidden: HIDDEN.indexOf(handle) !== -1 || !!(d && d.s === false)
    };
    if (!c.price) {
      // Prices come only from the feed (snippets/price-from.liquid, the one price source), which
      // covers the display rows. Without it a card shows no price rather than a guessed one; a
      // newly Active product outside the display rows needs a row there to get its overlay price.
      if (d) { c.price = plainText(d.price); }
    }
    var v = term ? variantFor(handle, sp, term) : null;
    // Variant photos are colour swatches, so keep the product photo and name the colour.
    if (v) { c.url += '?variant=' + v.id; c.line = v.title; c.available = v.a; }
    return c;
  }
  function card(c, area) {
    if (c.hidden) { return ''; }
    return '<a class="ikS-card' + (c.available === false ? ' is-soldout' : '') + '" href="' + esc(c.url) + '" data-ik-item="' + esc(area + ':' + c.handle) + '">' +
      '<span class="ikS-card__img">' + (c.img ? '<img src="' + esc(c.img) + '" alt="' + esc(c.name) + '" width="200" height="200" loading="lazy">' : '') + '</span>' +
      '<span class="ikS-card__text"><span class="ikS-card__name">' + esc(c.name) + '</span>' +
      (c.line ? '<span class="ikS-card__line">' + esc(c.line) + '</span>' : '') +
      (c.price ? '<span class="ikS-card__cost">' + esc(c.price) + '</span>' : '') +
      (c.available === false ? '<span class="ikS-card__note">Sold out</span>' : '') + '</span></a>';
  }
  function linkRow(l, area) {
    return '<a class="ikS-link" href="' + esc(l.url) + '" data-ik-item="' + esc(area + ':' + l.url) + '"><span><span class="ikS-link__t">' + esc(l.title) +
      '</span><span class="ikS-link__k">' + esc(l.type || '') + '</span></span><i aria-hidden="true">›</i></a>';
  }
  function answerCard(it, withButton) {
    var cta = it.cta && LINKS[it.cta] ? LINKS[it.cta] : null;
    var links = (it.links || []).map(function (k) { return LINKS[k]; }).filter(Boolean);
    if (cta && !withButton) { links.unshift(cta); cta = null; }
    return '<div class="ikS-answer" data-ik-sec="answer"><p class="ikS-answer__label">' + esc(it.label) + '</p>' +
      (it.answer ? '<p class="ikS-answer__text">' + esc(it.answer) + '</p>' : '') +
      '<div class="ikS-answer__acts">' +
      (cta ? '<a class="ikS-btn" href="' + esc(cta.url) + '" data-ik-item="answer:' + esc(cta.url) + '">' + esc(cta.title) + '</a>' : '') +
      links.map(function (l) { return '<a class="ikS-tlink" href="' + esc(l.url) + '" data-ik-item="answer:' + esc(l.url) + '">' + esc(l.title) + '</a>'; }).join('') +
      '</div></div>';
  }
  function finderCard(wide, area) {
    var copy = CFG.finder || {};
    return '<div class="ikS-finder' + (wide ? ' ikS-finder--wide' : '') + '" data-ik-sec="finder">' +
      '<div><p class="ikS-finder__title">' + esc(copy.title) + '</p>' +
      '<p class="ikS-finder__copy">' + esc(copy.copy) + '</p></div>' +
      '<a href="' + esc(localUrl('/pages/find-your-case')) + '" class="ikS-btn" data-ik-item="finder:' + esc(area) + '">' + esc(copy.cta) + '</a></div>';
  }
  function section(label, sec, inner, note) {
    return '<div class="ikS-sec" data-ik-sec="' + sec + '"><p class="ikS-eyebrow">' + esc(label) + '</p>' +
      (note ? '<p class="ikS-sec__note">' + esc(note) + '</p>' : '') + inner + '</div>';
  }
  function numberItems(container) {
    var items = container.querySelectorAll('[data-ik-item]');
    for (var i = 0; i < items.length; i++) { items[i].setAttribute('data-ik-pos', String(i + 1)); }
  }

  /* ---------- predictive search ---------- */
  var CACHE = {}, CACHE_KEYS = [];
  function suggest(term) {
    var k = norm(term);
    if (CACHE[k]) { return CACHE[k]; }
    // No tag filter in the query: curated products (MagNano, multi-week sets) are not tagged Active.
    // visibleProduct() applies the Active-or-curated policy to the returned products instead.
    var url = localUrl('/search/suggest.json') + '?q=' + encodeURIComponent(term) +
      '&resources[type]=product,collection,page,article,query&resources[limit]=10&resources[limit_scope]=each' +
      '&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,tag';
    // Content results (pages, guides) come from a separate request with no product fields.
    var contentUrl = localUrl('/search/suggest.json') + '?q=' + encodeURIComponent(term) +
      '&resources[type]=collection,page,article,query&resources[limit]=10&resources[limit_scope]=each';
    var content = fetchTimed(contentUrl, true).catch(function (e) {
      track('search_error', { stage: 'content', message: String((e && e.message) || e).slice(0, 80) });
      return {};
    });
    var p = Promise.all([fetchTimed(url.replace('product,collection,page,article,query', 'product'), true), content]).then(function (responses) {
      var products = responses[0] && responses[0].resources && responses[0].resources.results;
      var results = (responses[1] && responses[1].resources && responses[1].resources.results) || {};
      results.products = products && products.products || [];
      return results;
    }).catch(function (e) {
      delete CACHE[k];
      track('search_error', { stage: 'suggest', message: String((e && e.message) || e).slice(0, 80) });
      return null;
    });
    CACHE[k] = p; CACHE_KEYS.push(k);
    if (CACHE_KEYS.length > 60) { delete CACHE[CACHE_KEYS.shift()]; }
    return p;
  }
  function visibleProducts(r) {
    return ((r && r.products) || []).filter(function (p) {
      return visibleProduct(p) && ((DATA && DATA[p.handle] && DATA[p.handle].i) || (p.featured_image && p.featured_image.url));
    });
  }
  // Fetch with the typo-fixed term; if no case matches, retry without filler words, then word by word.
  function fetchBest(term, intents) {
    var used = correct(term);
    return suggest(used).then(function (r) {
      if (r === null) { return { used: used, r: null, failed: true }; }
      if (visibleProducts(r).length || intents.length || !FLAGS.relax) { return { used: used, r: r }; }
      var tries = [], rx = relax(used);
      if (rx) { tries.push(rx); }
      var ws = sigWords(used).filter(function (w) { return w.length > 2; }).sort(function (a, b) { return b.length - a.length; });
      if (ws.length > 1) { tries = tries.concat(ws.slice(0, 3)); }
      var first = r;
      function next(i) {
        if (i >= tries.length) { return { used: used, r: first }; }
        return suggest(tries[i]).then(function (r2) {
          if (r2 && visibleProducts(r2).length) { return { used: tries[i], r: r2, relaxed: true }; }
          return next(i + 1);
        });
      }
      return next(0);
    });
  }

  /* ---------- model ---------- */
  function buildModel(term, fetched) {
    var used = fetched ? fetched.used : correct(term);
    var intents = matchIntents(used);
    var primary = intents[0] || null;
    var secondary = null;
    for (var i = 1; i < intents.length; i++) {
      var it = intents[i];
      if ((it.products && !(primary.products)) || (it.answer && it.kind !== primary.kind)) { secondary = it; break; }
    }
    var shown = [primary, secondary].filter(Boolean);
    var answer = null, finder = false, intentProducts = [], intentLabel = '', intentNote = '';
    shown.forEach(function (it) {
      if (!answer && (it.kind === 'service' || it.kind === 'fit') && (it.answer || it.cta || (it.links && it.links.length))) { answer = it; }
      if (it.finder) { finder = true; }
      if (it.products && !intentProducts.length) {
        intentProducts = it.products.filter(function (h) { return visibleProduct({ handle: h }); }).slice(0, 4);
        intentLabel = it.productsLabel || it.label;
        intentNote = it.note || '';
      }
    });
    var r = (fetched && fetched.r) || {};
    var products = rankProducts(visibleProducts(r), used).filter(function (p) { return intentProducts.indexOf(p.handle) === -1; });

    var seen = {}, links = [];
    function mark(l) { seen[urlKey(l.url)] = 1; seen['t:' + norm(l.title)] = 1; }
    function add(l) {
      if (!l || seen[urlKey(l.url)] || seen['t:' + norm(l.title)]) { return; }
      mark(l); links.push(l);
    }
    if (answer) {
      if (answer.cta && LINKS[answer.cta]) { mark(LINKS[answer.cta]); }
      (answer.links || []).forEach(function (k) { if (LINKS[k]) { mark(LINKS[k]); } });
    }
    if (finder) { mark(LINKS.find); }
    shown.forEach(function (it) { if (it !== answer) { (it.links || []).forEach(function (k) { add(LINKS[k]); }); } });
    var byUrl = {};
    Object.keys(LINKS).forEach(function (k) { byUrl[urlKey(LINKS[k].url)] = LINKS[k]; });
    (r.collections || []).forEach(function (c) {
      if ((CFG.allowedCollections || []).indexOf(c.handle) > -1) { add(byUrl[urlKey(c.url)] || { title: c.title, url: urlKey(c.url), type: 'Collection' }); }
    });
    (r.pages || []).forEach(function (p) {
      if ((CFG.allowedPages || []).indexOf(p.handle) > -1) { add(byUrl[urlKey(p.url)] || { title: p.title, url: urlKey(p.url), type: 'Page' }); }
    });
    var qw = sigWords(used).filter(function (w) { return w.length > 2; }), nArticles = 0;
    (r.articles || []).forEach(function (a) {
      var tw = words(a.title);
      if (nArticles < 2 && qw.some(function (w) { return tw.some(function (x) { return x.indexOf(w) === 0; }); })) {
        nArticles++; add({ title: a.title, url: urlKey(a.url), type: 'Article' });
      }
    });

    // Suggested searches: only words the store actually uses, never hidden products.
    var vocab = {};
    visibleProducts(r).forEach(function (p) { words(p.title).forEach(function (w) { vocab[w] = 1; }); });
    VLIST.forEach(function (w) { if (VOCAB[w] >= 2) { vocab[w] = 1; } });
    words(used).forEach(function (w) { vocab[w] = 1; });
    var hiddenNames = HIDDEN.map(norm);
    var queries = (r.queries || []).map(function (x) { return x.text; }).filter(function (t) {
      var n = norm(t);
      return n && n !== norm(used) && n !== norm(term) && hiddenNames.indexOf(n) === -1 &&
        !/engraving|secret/.test(n) && words(n).every(function (w) { return w.length < 3 || vocab[w]; });
    }).slice(0, 4);

    return {
      term: term, used: used,
      corrected: fetched && fetched.exact ? '' : (correct(term) !== term ? correct(term) : ''),
      relaxed: !!(fetched && fetched.relaxed),
      intents: intents, primary: primary, answer: answer, finder: finder,
      intentProducts: intentProducts, intentLabel: intentLabel, intentNote: intentNote,
      products: products, links: links.slice(0, 5), queries: queries,
      complete: !!(fetched && fetched.r && !fetched.failed),
      exact: !!(fetched && fetched.exact),
      empty: !answer && !intentProducts.length && !products.length && !links.length
    };
  }

  /* ---------- typed state ---------- */
  function noteHtml(m) {
    if (m.relaxed) {
      return '<p class="ikS-note">No exact match for &ldquo;' + esc(m.term) + '&rdquo;. Showing results for &ldquo;' + esc(m.used) + '&rdquo;.</p>';
    }
    if (m.corrected) {
      return '<p class="ikS-note">Showing results for &ldquo;<strong>' + esc(m.used) + '</strong>&rdquo;. <button type="button" class="ikS-tlink" data-ik-exact="' + esc(m.term) + '">Search for &ldquo;' + esc(m.term) + '&rdquo; instead</button></p>';
    }
    return '';
  }
  function renderTyped(m) {
    var main = '', finderShown = false, budget = 8;
    var serviceLead = m.primary && m.primary.kind === 'service';
    // Fit questions and big pills: the quiz leads (it matches case and size in 3 questions).
    if (m.finder && FLAGS.finderFirst !== false) { main += finderCard(true, 'intent'); finderShown = true; }
    if (m.answer) { main += answerCard(m.answer, !finderShown); }
    if (m.intentProducts.length) {
      var ic = m.intentProducts.slice(0, budget).map(function (h) { return card(cardData(h, null, m.used), 'intent'); });
      budget -= ic.length;
      main += section(m.intentLabel || 'Best match', 'intent', '<div class="ikS-grid">' + ic.join('') + '</div>', m.intentNote);
    }
    if (m.products.length && budget > 0) {
      var n = m.intentProducts.length || serviceLead ? Math.min(4, budget) : budget;
      var pc = m.products.slice(0, n).map(function (p) { return card(cardData(p.handle, p, m.used), 'product'); });
      main += section(m.intentProducts.length ? 'More cases' : 'Products', 'products', '<div class="ikS-grid">' + pc.join('') + '</div>');
    }
    var side = '';
    if (m.queries.length) {
      side += '<div data-ik-sec="suggestions"><p class="ikS-eyebrow">Suggestions</p><div class="ikS-sugg">' + m.queries.map(function (t) {
        var typed = norm(m.term), low = String(t).toLowerCase();
        var html = low.indexOf(typed) === 0 ? esc(t.slice(0, typed.length)) + '<b>' + esc(t.slice(typed.length)) + '</b>' : '<b>' + esc(t) + '</b>';
        return '<button type="button" data-ik-chip="' + esc(t) + '">' + html + '</button>';
      }).join('') + '</div></div>';
    }
    if (m.links.length) {
      side += '<div data-ik-sec="links"><p class="ikS-eyebrow' + (side ? ' ikS-eyebrow--gap' : '') + '">Pages &amp; help</p>' +
        m.links.map(function (l) { return linkRow(l, 'link'); }).join('') + '</div>';
    }
    // Shopping searches with few cases get the quiz as a side card (one mint card per screen).
    if (!finderShown && !serviceLead && m.products.length + m.intentProducts.length < 4) {
      side += '<div class="ikS-side-finder">' + finderCard(false, 'side') + '</div>';
    }
    return noteHtml(m) +
      '<div class="ikS-cols ikS-cols--typed' + (side ? '' : ' ikS-cols--solo') + '"><div class="ikS-col ikS-col--main">' + main + '</div>' +
      (side ? '<div class="ikS-col ikS-col--aside">' + side + '</div>' : '') + '</div>' +
      '<div class="ikS-all"><a class="ikS-all__link" href="' + esc(searchUrl(m.used, m.used !== m.term ? m.term : '')) + '" data-ik-item="see-all:' + esc(m.used) + '">See all results for &ldquo;<span>' + esc(m.used) + '</span>&rdquo; <i aria-hidden="true">›</i></a></div>';
  }

  /* ---------- recent searches ---------- */
  function recentList() {
    try { var a = JSON.parse(ls('ikS_recent') || '[]'); return Array.isArray(a) ? a.slice(0, 5) : []; } catch (e) { return []; }
  }
  function remember(term) {
    if (!FLAGS.recent) { return; }
    var t = String(term || '').trim().slice(0, 60);
    if (norm(t).length < 2) { return; }
    var a = recentList().filter(function (x) { return norm(x) !== norm(t); });
    a.unshift(t);
    ls('ikS_recent', JSON.stringify(a.slice(0, 5)));
  }

  /* ---------- overlay ---------- */
  function initOverlay(root) {
    var popup = document.getElementById('searchPopup');
    var form = root.querySelector('[data-ik-form]');
    var input = root.querySelector('[data-ik-input]');
    var bar = form;
    var clearBtn = root.querySelector('[data-ik-clear]');
    var openEl = root.querySelector('[data-ik-open]');
    var resultsEl = root.querySelector('[data-ik-results]');
    var nomatchEl = root.querySelector('[data-ik-nomatch]');
    var noresEl = root.querySelector('[data-ik-nores]');
    var statusEl = root.querySelector('[data-ik-status]');
    var recentEl = root.querySelector('[data-ik-recent]');
    var bsEl = root.querySelector('[data-ik-bestsellers]');
    var openFinder = root.querySelector('[data-ik-openfinder]');
    var timer = null, spinTimer = null, phTimer = null, reqId = 0;
    var S = null, opener = null, isOpen = false, exactTerm = null, legacyExposure = null;

    function overlayTrack(name, params) { track(name, params, 'v3', S && S.exposure); }
    function legacyCommit(outcome) {
      if (!legacyExposure || legacyExposure.committed) { return; }
      var field = popup.querySelector('.ikS-legacy-ui .search__input');
      var term = field ? field.value.trim() : '';
      if (!term) { return; }
      legacyExposure.committed = true;
      track('search', { search_term: term.slice(0, 100), results_count: null, results_status: 'unknown', outcome: outcome, source: 'legacy_popup' }, 'legacy', legacyExposure);
    }
    function cancelWork() {
      clearTimeout(timer); timer = null; reqId++;
      spinner(false);
    }

    function newSession(trigger) {
      S = { openedAt: Date.now(), model: null, refinements: 0, committed: false, trigger: trigger, exposure: expose('v3') };
    }
    function commit(outcome) {
      if (!S || S.committed) { return; }
      var term = input.value.replace(/\s+/g, ' ').trim();
      if (!term) { return; }
      if (!S.model || norm(S.model.term) !== norm(term)) { S.model = buildModel(term, { used: exactTerm === term ? term : correct(term), r: null, exact: exactTerm === term }); }
      S.committed = true;
      var m = S.model;
      var p = {
        search_term: m.term.slice(0, 100), search_term_used: m.used !== m.term ? m.used.slice(0, 100) : '',
        intent: m.intents.slice(0, 2).map(function (i) { return i.id; }).join(',') || 'none',
        results_count: m.complete ? m.products.length + m.intentProducts.length : null, links_count: m.complete ? m.links.length : null,
        results_status: m.complete ? 'complete' : 'unknown',
        answer: m.answer ? m.answer.id : 'none', outcome: outcome, refinements: S.refinements,
        corrected: m.corrected ? 1 : 0, relaxed: m.relaxed ? 1 : 0,
        seconds_open: Math.round((Date.now() - S.openedAt) / 1000), source: 'search_overlay'
      };
      overlayTrack('search', p);
      if (m.complete && m.empty) { overlayTrack('search_no_results', p); }
      if (outcome === 'click' || outcome === 'submit') { remember(m.term); }
    }

    function setStatus(t) { if (statusEl) { statusEl.textContent = t; } }
    function renderBestSellers() {
      if (!bsEl) { return; }
      loadData().then(function (d) {
        if (!d || !Object.keys(d).length) { bsEl.innerHTML = '<a class="ikS-tlink" href="' + esc(localUrl('/collections/all-pill-cases')) + '">Shop all cases</a>'; return; }
        bsEl.innerHTML = (CFG.bestSellers || []).map(function (h) { return card(cardData(h, null, ''), 'bestseller'); }).join('');
        numberItems(openEl);
      });
    }
    function renderRecent() {
      if (!recentEl) { return; }
      var a = FLAGS.recent ? recentList() : [];
      if (!a.length) { recentEl.hidden = true; recentEl.innerHTML = ''; return; }
      recentEl.innerHTML = '<div class="ikS-recent__head"><p class="ikS-eyebrow">Recent searches</p><button type="button" class="ikS-tlink ikS-tlink--s" data-ik-recent-clear>Clear</button></div>' +
        '<div class="ikS-chips">' + a.map(function (t) { return '<button type="button" class="ikS-chip" data-ik-chip="' + esc(t) + '">' + esc(t) + '</button>'; }).join('') + '</div>';
      recentEl.hidden = false;
    }
    function showOpen(noResultsFor) {
      resultsEl.hidden = true; resultsEl.innerHTML = '';
      openEl.hidden = false;
      root.classList.toggle('ikS--nomatch', !!noResultsFor);
      if (noResultsFor) {
        noresEl.innerHTML = '<strong>No exact match for &ldquo;' + esc(noResultsFor) + '&rdquo;</strong>Let us match you, or start with a routine or best seller.';
        nomatchEl.hidden = false;
        if (openFinder) { openFinder.hidden = true; }
        setStatus('No exact match for ' + noResultsFor);
      } else {
        setStatus('');
        nomatchEl.hidden = true;
        if (openFinder) { openFinder.hidden = false; }
        renderRecent();
      }
      renderBestSellers();
      numberItems(openEl);
    }
    function spinner(on) {
      clearTimeout(spinTimer);
      if (on) { spinTimer = setTimeout(function () { bar.classList.add('is-loading'); }, 180); }
      else { bar.classList.remove('is-loading'); }
    }
    function show(m) {
      if (S) {
        if (S.model && norm(S.model.term) !== norm(m.term)) { S.refinements++; }
        S.model = m;
      }
      if (m.empty) { showOpen(m.term); return; }
      openEl.hidden = true;
      root.classList.remove('ikS--nomatch');
      resultsEl.innerHTML = renderTyped(m);
      resultsEl.hidden = false;
      numberItems(resultsEl);
      var n = m.products.length + m.intentProducts.length;
      setStatus((m.answer ? m.answer.label + '. ' : '') + n + (n === 1 ? ' product' : ' products') + ' for ' + m.used);
    }
    function run(term, exact) {
      if (!isOpen) { return; }
      term = term.replace(/\s+/g, ' ').trim();
      exactTerm = exact ? term : null;
      clearBtn.hidden = input.value.length === 0;
      var id = ++reqId;
      if (norm(term).length < 2) { if (S) { S.model = null; } spinner(false); showOpen(); return; }
      var intents = matchIntents(exact ? term : correct(term));
      // Instant answer while the store search runs (first render only, to avoid flicker).
      if (FLAGS.instant && intents.length && resultsEl.hidden) {
        var mi = buildModel(term, { used: exact ? term : correct(term), r: null, exact: !!exact });
        if (!mi.empty) { show(mi); }
      }
      spinner(true);
      var search = exact ? suggest(term).then(function (r) { return { used: term, r: r, failed: r === null, exact: true }; }) : fetchBest(term, intents);
      Promise.all([search, freshData() ? null : Promise.race([loadData(), new Promise(function (r) { setTimeout(r, 600); })])]).then(function (out) {
        if (!isOpen || id !== reqId) { return; }
        spinner(false);
        var f = out[0];
        if (f.failed && !intents.length) { S.model = buildModel(term, f); showOpen(); return; }
        show(buildModel(term, f));
      });
    }
    function setTerm(t, exact) { cancelWork(); input.value = t; run(t, exact); input.focus(); }

    function lockScroll(on) {
      document.documentElement.classList.toggle('ik-search-open', on);
      document.body.classList.toggle('ik-search-open', on);
    }
    function onOpened(trigger) {
      dialogAttrs(true);
      if (isOpen) { return; }
      isOpen = true;
      cancelWork(); exactTerm = null;
      readFlags();
      newSession(trigger);
      input.value = ''; clearBtn.hidden = true; reqId++;
      showOpen();
      lockScroll(true);
      startPlaceholder();
      overlayTrack('search_open', { trigger: trigger, source: 'search_overlay' });
    }
    function onClosed() {
      dialogAttrs(false);
      if (!isOpen) { return; }
      isOpen = false;
      commit('close');
      cancelWork();
      lockScroll(false);
      stopPlaceholder();
      if (opener && typeof opener.focus === 'function') { try { opener.focus({ preventScroll: true }); } catch (e) {} }
    }
    // Dialog semantics only while the new search is open, so the closed popup (and the
    // control arm's markup) stays exactly as the live theme has it.
    function dialogAttrs(on) {
      if (on) { popup.setAttribute('role', 'dialog'); popup.setAttribute('aria-modal', 'true'); popup.setAttribute('aria-label', 'Search'); }
      else { popup.removeAttribute('role'); popup.removeAttribute('aria-modal'); popup.removeAttribute('aria-label'); }
    }
    function openPopup(trigger) {
      opener = document.activeElement;
      document.body.classList.add('showOverly');
      popup.classList.add('active');
      input.focus();
      onOpened(trigger);
    }
    function closePopup() {
      document.body.classList.remove('showOverly');
      popup.classList.remove('active');
      onClosed();
    }
    api.open = function (term) {
      if (applyVariant() === 'legacy') { return; }
      openPopup('api');
      if (term) { setTerm(term); }
    };
    api.close = closePopup;

    // Rotating example searches in the empty field.
    var phIdx = 0;
    function startPlaceholder() {
      stopPlaceholder();
      var list = CFG.placeholders || [];
      input.setAttribute('placeholder', list[0] || 'Search');
      if (!FLAGS.placeholder || list.length < 2 || reducedMotion()) { return; }
      phIdx = 0;
      phTimer = setInterval(function () {
        if (input.value) { return; }
        phIdx = (phIdx + 1) % list.length;
        input.setAttribute('placeholder', list[phIdx]);
      }, 2800);
    }
    function stopPlaceholder() { clearInterval(phTimer); phTimer = null; }

    input.addEventListener('input', function () {
      cancelWork(); exactTerm = null;
      clearBtn.hidden = input.value.length === 0;
      var v = input.value;
      timer = setTimeout(function () { run(v); }, 120);
    });
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var term = input.value.replace(/\s+/g, ' ').trim();
      if (!term) { input.focus(); return; }
      var exact = exactTerm === term, used = exact ? term : correct(term);
      if (!S) { newSession('submit'); }
      if (!S.model || norm(S.model.term) !== norm(term)) { S.model = buildModel(term, { used: used, r: null, exact: exact }); }
      commit('submit');
      cancelWork();
      window.location.href = searchUrl(used, used !== term ? term : '') + (exact ? '&ik_exact=1' : '');
    });
    clearBtn.addEventListener('click', function () { setTerm(''); });
    root.addEventListener('click', function (e) {
      var ex = e.target.closest('[data-ik-exact]');
      if (ex) {
        e.preventDefault();
        var t = ex.getAttribute('data-ik-exact');
        setTerm(t, true);
        return;
      }
      if (e.target.closest('[data-ik-recent-clear]')) { ls('ikS_recent', null); renderRecent(); input.focus(); return; }
      var chip = e.target.closest('[data-ik-chip]');
      if (chip) { e.preventDefault(); setTerm(chip.getAttribute('data-ik-chip')); return; }
      var a = e.target.closest('a[data-ik-item]');
      if (a) {
        var sec = a.closest('[data-ik-sec]');
        var term = input.value.trim();
        overlayTrack('select_content', {
          content_type: 'search_result', item_id: a.getAttribute('data-ik-item').slice(0, 100),
          section: sec ? sec.getAttribute('data-ik-sec') : 'other',
          position: Number(a.getAttribute('data-ik-pos') || 0),
          search_term: term ? (S && S.model ? S.model.term : term).slice(0, 100) : '(empty)',
          state: term ? 'typed' : 'open', source: 'search_overlay'
        });
        if (term) { commit('click'); }
      }
    });
    root.querySelector('[data-ik-close]').addEventListener('click', function (e) { e.preventDefault(); closePopup(); });

    // Keyboard: arrows move through results, Esc closes, Tab stays inside the dialog.
    function navItems() {
      var scope = resultsEl.hidden ? openEl : resultsEl;
      return Array.prototype.filter.call(scope.querySelectorAll('a[href], button'), function (el) {
        return el.offsetParent !== null && !el.closest('[hidden]');
      });
    }
    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        var items = navItems();
        if (!items.length) { return; }
        var i = items.indexOf(document.activeElement);
        if (e.key === 'ArrowDown') { (i < 0 ? items[0] : items[Math.min(i + 1, items.length - 1)]).focus(); }
        else if (i <= 0) { input.focus(); }
        else { items[i - 1].focus(); }
        e.preventDefault();
      } else if (e.key === 'Tab') {
        var all = Array.prototype.filter.call(root.querySelectorAll('a[href], button, input:not([type=hidden])'), function (el) {
          return el.offsetParent !== null && !el.closest('[hidden]') && !el.hidden;
        });
        if (!all.length) { return; }
        var first = all[0], last = all[all.length - 1];
        if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
        else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) { closePopup(); return; }
      if (e.key === '/' && !isOpen && !e.metaKey && !e.ctrlKey && !e.altKey) {
        var t = e.target;
        if (t && (t.isContentEditable || /^(input|textarea|select)$/i.test(t.tagName))) { return; }
        if (applyVariant() === 'legacy') { return; }
        e.preventDefault();
        openPopup('shortcut');
      }
    });

    // Open: capture phase, so it runs before the theme's handler (which stops propagation).
    // Focusing here, inside the tap, is what brings up the phone keyboard.
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest ? e.target.closest('.searchIco, [data-search-open]') : null;
      if (!t) { return; }
      if (applyVariant() === 'legacy') {
        if (!popup.classList.contains('active')) { legacyExposure = expose('legacy'); track('search_open', { trigger: 'icon', source: 'legacy_popup' }, 'legacy', legacyExposure); }
        return;
      }
      if (t.matches('[data-search-open]')) { e.preventDefault(); }
      opener = t;
      document.body.classList.add('showOverly');
      popup.classList.add('active');
      input.focus();
      onOpened('icon');
    }, true);
    var legacyForm = popup.querySelector('.ikS-legacy-ui form');
    if (legacyForm) {
      legacyForm.addEventListener('submit', function () { legacyCommit('submit'); }, true);
      legacyForm.addEventListener('click', function (e) {
        var a = e.target.closest && e.target.closest('a[href*="/products/"]');
        if (!a) { return; }
        var field = legacyForm.querySelector('.search__input');
        track('select_content', { content_type: 'search_result', item_id: 'product:' + urlKey(a.getAttribute('href')).split('/').pop(), search_term: field ? field.value.slice(0, 100) : '', state: 'typed', source: 'legacy_popup' }, 'legacy', legacyExposure);
        legacyCommit('click');
      }, true);
    }
    // Warm the photo feed when a shopper heads for search.
    ['pointerenter', 'touchstart', 'focusin'].forEach(function (ev) {
      document.addEventListener(ev, function (e) {
        if (e.target && e.target.closest && e.target.closest('.searchIco') && variant() !== 'legacy') { loadData(); }
      }, { capture: true, passive: true });
    });
    // The theme closes the popup itself (overlay click); watch for it.
    if (window.MutationObserver) {
      new MutationObserver(function () {
        if (!popup.classList.contains('active')) { onClosed(); legacyCommit('close'); legacyExposure = null; }
        else if (!isOpen && !document.documentElement.classList.contains('ikS-legacy')) { onOpened('other'); }
      }).observe(popup, { attributes: true, attributeFilter: ['class'] });
    }
    window.addEventListener('pagehide', function () { if (isOpen) { commit('leave'); } legacyCommit('leave'); });

    api.explain = function (term) {
      return fetchBest(term, matchIntents(correct(term))).then(function (f) {
        var m = buildModel(term, f);
        return {
          term: m.term, used: m.used, corrected: m.corrected, relaxed: m.relaxed,
          intents: m.intents.map(function (i) { return i.id; }), answer: m.answer && m.answer.id, finder: m.finder,
          intentProducts: m.intentProducts, products: m.products.map(function (p) { return p.handle; }),
          links: m.links.map(function (l) { return l.url; }), queries: m.queries, empty: m.empty
        };
      });
    };
  }

  /* ---------- results page (/search) ---------- */
  function initResultsPage(page) {
    var term = page.getAttribute('data-q') || '';
    var count = Number(page.getAttribute('data-count') || 0);
    var performed = page.getAttribute('data-performed') === 'true';
    var from = '';
    try { from = new URLSearchParams(window.location.search).get('ik_from') || ''; } catch (e) {}
    var assist = page.querySelector('[data-ik-assist]');
    var noteEl = page.querySelector('[data-ik-note]');
    var noresEl = page.querySelector('[data-ik-nores]');
    var exposure = expose('v3');
    var exact = /[?&]ik_exact=1/.test(window.location.search);
    var used = exact ? term : correct(term);
    var m = buildModel(term, { used: used, r: {}, exact: exact });
    var total = Number(page.getAttribute('data-total') || count);
    var more = page.getAttribute('data-more') === 'true';
    var hasLinks = page.getAttribute('data-links') === '1';
    function pageTrack(name, params) { track(name, params, 'v3', exposure); }

    function markFinder() {
      if (page.querySelector('.ikS-finder')) { document.body.classList.add('ikS-has-finder'); }
    }
    markFinder();
    numberItems(page);
    page.addEventListener('click', function (e) {
      var ex = e.target.closest('[data-ik-exact]');
      if (ex) { e.preventDefault(); window.location.href = searchUrl(ex.getAttribute('data-ik-exact')) + '&ik_exact=1'; return; }
      var a = e.target.closest('a[data-ik-item]');
      if (!a) { return; }
      var sec = a.closest('[data-ik-sec]');
      pageTrack('select_content', {
        content_type: 'search_result', item_id: a.getAttribute('data-ik-item').slice(0, 100),
        section: sec ? sec.getAttribute('data-ik-sec') : 'other', position: Number(a.getAttribute('data-ik-pos') || 0),
        search_term: term.slice(0, 100), state: 'results_page', source: 'results_page'
      });
      remember(term);
    });
    if (!performed || norm(term).length < 2) { return; }

    // Rule-based help and telemetry must work even if the photo feed never arrives.
    function renderAssist() {
      m = buildModel(term, { used: used, r: {}, exact: exact });
      var html = '';
      var nomatchFinder = page.querySelector('[data-ik-nomatch] .ikS-finder');
      var quizFirst = m.finder && FLAGS.finderFirst !== false;
      if (nomatchFinder) { nomatchFinder.hidden = quizFirst; }
      if (quizFirst) { html += finderCard(true, 'intent'); }
      if (m.answer) { html += answerCard(m.answer, !quizFirst && !nomatchFinder); }
      if (m.intentProducts.length) {
        html += section(m.intentLabel, 'intent', '<div class="ikS-grid">' + m.intentProducts.map(function (h) { return card(cardData(h, null, term), 'intent'); }).join('') + '</div>', m.intentNote);
      }
      assist.innerHTML = html;
      if (noresEl && (m.answer || m.intentProducts.length)) { noresEl.hidden = true; }
      markFinder();
      numberItems(page);
    }
    renderAssist();
    var grid = page.querySelector('[data-ik-sec="results"] .ikR-grid');
    if (grid && m.intentProducts.length) {
      Array.prototype.forEach.call(grid.querySelectorAll('[data-ik-item]'), function (a) {
        var h = (a.getAttribute('data-ik-item') || '').split(':').pop();
        if (m.intentProducts.indexOf(h) > -1) { a.remove(); }
      });
      if (!grid.children.length) { grid.closest('[data-ik-sec="results"]').hidden = true; }
    }
    var view = {
      search_term: term.slice(0, 100), search_term_from: from.slice(0, 100), results_count: count,
      total_matches: total, results_scope: 'current_page', has_more_results: more ? 1 : 0,
      no_results: count === 0 && !m.answer && !m.finder && !m.intentProducts.length && !hasLinks && !more ? 1 : 0,
      intent: m.intents.slice(0, 2).map(function (i) { return i.id; }).join(',') || 'none', source: 'results_page'
    };
    // Nothing matched: retry once with the typo-fixed or trimmed search.
    if (total === 0 && !hasLinks && !m.intents.length && !from && !exact) {
      var fixed = correct(term);
      var alt = fixed !== term ? fixed : (FLAGS.relax ? relax(term) : '');
      if (alt && norm(alt) !== norm(term)) {
        pageTrack('search_results_view', Object.assign({ redirected_to: alt.slice(0, 100) }, view));
        window.location.replace(searchUrl(alt, term));
        return;
      }
    }
    pageTrack('search_results_view', view);
    if (view.no_results) { pageTrack('search_no_results', view); }

    if (from && noteEl) {
      noteEl.innerHTML = 'Showing results for &ldquo;<strong>' + esc(term) + '</strong>&rdquo;. <button type="button" class="ikS-tlink" data-ik-exact="' + esc(from) + '">Search for &ldquo;' + esc(from) + '&rdquo; instead</button>';
      noteEl.hidden = false;
    }
    loadData().then(renderAssist);
  }

  /* ---------- legacy results page (A/B control): record views and clicks only ---------- */
  function initLegacyResults() {
    var exposure = expose('legacy');
    var raw = '';
    try { raw = new URLSearchParams(window.location.search).get('q') || ''; } catch (e) {}
    var term = raw.replace(/tag:Active\s*/i, '').trim();
    var n = document.querySelectorAll('.grid-products .grid__item').length;
    track('search_results_view', { search_term: term.slice(0, 100), results_count: n, results_scope: 'current_page', no_results: n ? 0 : 1, intent: 'none', source: 'legacy_results_page' }, 'legacy', exposure);
    if (!n) { track('search_no_results', { search_term: term.slice(0, 100), source: 'legacy_results_page' }, 'legacy', exposure); }
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('.grid-products a[href*="/products/"]');
      if (!a) { return; }
      var items = Array.prototype.slice.call(document.querySelectorAll('.grid-products .grid__item'));
      track('select_content', { content_type: 'search_result', item_id: 'result:' + urlKey(a.getAttribute('href')).split('/').pop(), section: 'results', position: items.indexOf(a.closest('.grid__item')) + 1, search_term: term.slice(0, 100), state: 'results_page', source: 'legacy_results_page' }, 'legacy', exposure);
    });
  }

  api.variant = variant;
  // Console QA override only. Intelligems assignments are never persisted here;
  // use window.IKG_SEARCH or the configured experiment/group IDs for the test.
  api.setVariant = function (v) {
    ss(QA_KEY, v === null ? null : (v === 'legacy' ? 'legacy' : 'v3'));
    applyVariant();
    reconcileAssignments();
  };
  api.flags = function () { readFlags(); return FLAGS; };
  api.debug = function (on) { DEBUG = on !== false; ss('ikgSearchDebug', DEBUG ? '1' : null); return DEBUG; };
  api.correct = correct;
  api.intents = function (t) { return matchIntents(correct(t)).map(function (i) { return i.id; }); };

  // Only a KNOWN control visitor (QA override or Intelligems group = legacy) is sent back to the
  // old page; while Intelligems is still loading nobody is moved. Re-checked when it reports.
  function leaveNewResultsPage() {
    if (!document.getElementById('IkSearchPage') || !/[?&]view=ik(&|$)/.test(window.location.search)) { return false; }
    var m = window.location.search.match(/[?&]ikgsearch=(legacy|v3)(&|$)/);
    var v = (m && m[1]) || ss(QA_KEY) || assignment().variant;
    if (v !== 'legacy') { return false; }
    var q = window.location.search.replace(/([?&])view=ik(&|$)/, function (x, a, b) { return b ? a : ''; }).replace(/^\?$/, '');
    window.location.replace(window.location.pathname + q + window.location.hash);
    return true;
  }
  function boot() {
    applyVariant();
    var root = document.querySelector('[data-ik-search]');
    if (root && document.getElementById('searchPopup')) { initOverlay(root); }
    var page = document.getElementById('IkSearchPage');
    if (page) {
      // A control (legacy) visitor who opens a shared ?view=ik link gets the old page, so each
      // arm only ever sees its own results page. QA overrides (?ikgsearch=v3) still show it.
      if (leaveNewResultsPage()) { return; }
      initResultsPage(page);
    }
    else if (document.body && document.body.classList.contains('template-search') && /tag:Active/i.test(decodeURIComponent(window.location.search.replace(/\+/g, ' ')))) { initLegacyResults(); }
    // Prefetch only on search intent; the control and visitors who never search
    // do not need a background catalog request.
    reconcileAssignments();
  }
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', boot); } else { boot(); }
})();
